import React, { Component } from "react";
import { style, classes } from "typestyle";
import { Cream, Pink } from "@traceair/tds";

export const LIST_ROW_COLOR_INFO = "LIST_ROW_COLOR_INFO";
export const LIST_ROW_COLOR_ERROR = "LIST_ROW_COLOR_ERROR";

interface Props<T> {
  renderRightColumn?: (row?: T) => JSX.Element;
  renderLeftColumn?: (row?: T) => JSX.Element;
  rows: T[];
  renderRowColor?: (r: T) => string;
  rightColumnHeader?: string | null;
  leftColumnHeader?: string | null;
  sortBy?: "left" | "right" | null;
  onSort?: (rows: T[]) => T[];
  onRowClick?: (row: T) => void;
}
export default class ListView<T> extends Component<Props<T>> {
  render(): JSX.Element | null {
    const {
      renderLeftColumn,
      renderRightColumn,
      rows,
      renderRowColor,
      rightColumnHeader,
      leftColumnHeader,
      onSort,
      onRowClick,
    } = this.ensureProps(this.props);

    const sortedRows = onSort(rows);

    if (!sortedRows.length) {
      return null;
    }

    return (
      <div className={style({ width: "100%" })}>
        <ListViewHeader
          leftColumnHeader={leftColumnHeader}
          rightColumnHeader={rightColumnHeader}
        />
        <div
          className={style({
            boxShadow:
              "0 1px 6px rgba(0, 0, 0, 0.12), 0 1px 4px rgba(0, 0, 0, 0.24)",
            borderRadius: "12px",
            background: "white",
            $nest: {
              "&>*:first-child": {
                borderTopLeftRadius: "12px",
                borderTopRightRadius: "12px",
              },
              "&>*:last-child": {
                borderBottomLeftRadius: "12px",
                borderBottomRightRadius: "12px",
              },
            },
          })}
        >
          {sortedRows.map((row, i) => (
            <ListViewRow
              onClick={() => onRowClick(row)}
              className={getRowColorClassName(renderRowColor(row) || "")}
              leftColumn={renderLeftColumn(row)}
              rightColumn={renderRightColumn(row)}
              key={i}
            />
          ))}
        </div>
      </div>
    );

    function getRowColorClassName(color: string): string {
      switch (color) {
        case LIST_ROW_COLOR_INFO:
          return style({
            background: Cream,
            $nest: {
              "&:hover": {
                background: Cream,
              },
            },
          });
        case LIST_ROW_COLOR_ERROR:
          return style({
            background: Pink,
            $nest: {
              "&:hover": {
                background: Pink,
              },
            },
          });
        default:
          return "";
      }
    }
  }

  ensureProps(props: Props<T>) {
    // problem using defaultProps
    // https://github.com/facebook/flow/issues/2093

    const defaultProps = {
      renderLeftColumn: (row: T) => null,
      renderRightColumn: (row: T) => null,
      rightColumnHeader: null,
      leftColumnHeader: null,
      sortBy: null,
      onSort: (rows: T[]) => rows,
      renderRowColor: (row: T) => null,
      onRowClick: (row: T) => undefined,
    };

    return {
      ...defaultProps,
      ...props,
    };
  }
}

type ListViewHeaderProps = {
  rightColumnHeader?: string | null;
  leftColumnHeader?: string | null;
};
const ListViewHeader = (props: ListViewHeaderProps) => (
  <div className={classes(rowCN, rowHeaderCN)}>
    <div className={cellCN}>{props.leftColumnHeader || null}</div>
    <div className={classes(cellCN, style({ justifyContent: "flex-end" }))}>
      {props.rightColumnHeader || null}
    </div>
  </div>
);

type ListViewRowProps = {
  leftColumn?: JSX.Element | null;
  rightColumn?: JSX.Element | null;
  className?: string | null;
  onClick?: () => void;
};
const ListViewRow = (props: ListViewRowProps) => (
  <div
    className={classes(rowCN, rowRegularCN, props.className || "")}
    onClick={props.onClick || (() => null)}
  >
    <div className={cellCN}>{props.leftColumn || null}</div>
    {props.rightColumn && (
      <div
        className={classes(
          cellCN,
          style({ justifyContent: "flex-end", flex: "none", width: "260px" }),
        )}
      >
        {props.rightColumn}
      </div>
    )}
  </div>
);

const rowCN = style({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
});
const rowHeaderCN = style({
  fontWeight: 600,
  fontSize: "14px",
  textTransform: "none",
});
const rowRegularCN = style({
  cursor: "pointer",
  fontSize: "16px",
  borderBottom: "1px solid #eeeeee",
  $nest: {
    "&:hover": {
      background: "whitesmoke",
    },
    "&:last-child": {
      borderBottom: "none",
    },
  },
});
const cellCN = style({
  display: "flex",
  alignItems: "center",
  flex: 1,
  padding: "12px 24px",
  minHeight: "70px",
});
